import { useCallback, useEffect, useRef, useState } from 'react';
import { usePusherContext } from 'contexts/PusherContext';
import { AsyncJobStatus } from 'types/api';
import {
  useLazyGetJobQuery,
  useLazyGetJobStatusQuery,
  ReduxJob,
} from 'store/slices/apiV1/utility';
import { PusherEvent } from 'pusher-js/types/src/core/connection/protocol/message-types';
import {
  AsyncRequestHookType,
  AsyncRequestFunctionType,
  AsyncResponseStateType,
} from 'hooks/useAsyncRequestSession';
import { useInterval } from 'hooks/useInterval';
import useSnackbar from 'hooks/useSnackbar';

const JOB_STATUS_POLL_INTERVAL = 5 * 1000;
const DEFAULT_DELAY_BEFORE_POLL_JOB_STATUS = 10 * 1000;

type JobOptions = Partial<Omit<ReduxJob['options'], 'persistenceType'>>;

/**
 * This hook mimics the behavior of Redux Toolkit Query's lazy queries and mutations.
 * It returns a function that, when invoked, returns a promise. This allows for the handling of asynchronous operations
 * and the utilization of the resulting data once the promise resolves.
 */
export const useAsyncRequestComponent = <RequestType, ResponseType>(
  requestHook: AsyncRequestHookType<RequestType>
): [
  AsyncRequestFunctionType<RequestType, ResponseType>,
  AsyncResponseStateType<ResponseType>
] => {
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [jobOptions, setJobOptions] = useState<JobOptions>({});
  const [jobMessage, setJobMessage] = useState<string | null>(null);
  const { jobsChannel } = usePusherContext();
  const [requestDataQuery, requestQueryInfo] = requestHook();
  const [retrieveData, jobQueryInfo] = useLazyGetJobQuery();
  const [getJobStatus] = useLazyGetJobStatusQuery();

  const jobIdRef = useRef<string | null>(null);
  const timeoutIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bindChannelRef = useRef<() => void>(() => {});
  const unbindChannelRef = useRef<() => void>(() => {});
  const promiseRef = useRef<{
    resolve: (value?: ResponseType) => void;
    reject: (reason?: unknown) => void;
  }>({
    resolve: () => {},
    reject: () => {},
  });

  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const handlePusherMessage = useCallback(
    async (
      __: PusherEvent,
      eventData: { job_id: string; status: string; message?: string }
    ) => {
      if (
        eventData.job_id === jobIdRef.current &&
        (eventData.status === AsyncJobStatus.done ||
          eventData.status === AsyncJobStatus.error)
      ) {
        fetchData(eventData.job_id);
        clearPollJobStatusInterval();
      } else if (
        eventData.job_id === jobIdRef.current &&
        eventData.status === AsyncJobStatus.running
      ) {
        setJobMessage(eventData.message ?? null);
      }
      return null;
    },
    []
  );

  const handleErrorResponse = useCallback(
    (error: unknown, options: JobOptions) => {
      setIsFetching(false);
      setJobMessage(null);
      promiseRef.current.reject(error);
      jobIdRef.current = null;
      options.onError?.();
      options.onErrorSnackbar && dispatchErrorSnackbar(options.onErrorSnackbar);
    },
    [setIsFetching, dispatchErrorSnackbar]
  );

  const handleSuccessResponse = useCallback(
    (data: ResponseType, options: JobOptions) => {
      setIsFetching(false);
      setJobMessage(null);
      promiseRef.current.resolve(data);
      jobIdRef.current = null;
      const { message, navigateTo, customAction } =
        options.onCompleteSnackbar ?? {};
      options.onCompleteSnackbar &&
        dispatchSuccessSnackbar(message ?? '', navigateTo, customAction);
      options.onSuccess?.(data);
    },
    [setIsFetching, promiseRef, jobIdRef, dispatchSuccessSnackbar]
  );

  const fetchData = useCallback(
    async (id: string) => {
      try {
        const fetchedData = await retrieveData({
          jobId: id,
        }).unwrap();
        jobIdRef.current = null;
        unbindChannelRef.current();
        if (fetchedData?.status === AsyncJobStatus.error) {
          handleErrorResponse(fetchedData?.data, jobOptions);
          return;
        }
        handleSuccessResponse(fetchedData?.data, jobOptions);
      } catch (err) {
        handleErrorResponse(err, jobOptions);
      }
    },
    [retrieveData, jobOptions]
  );

  const bindChannel = useCallback(() => {
    jobsChannel.bind_global(handlePusherMessage);
  }, [jobsChannel, handlePusherMessage]);

  const unbindChannel = useCallback(() => {
    jobsChannel.unbind_global(handlePusherMessage);
  }, [jobsChannel, handlePusherMessage]);

  bindChannelRef.current = bindChannel;
  unbindChannelRef.current = unbindChannel;

  const requestData: AsyncRequestFunctionType<RequestType, ResponseType> =
    useCallback(
      async (request: RequestType, options: JobOptions = {}) => {
        const {
          delayBeforePollJobStatus = DEFAULT_DELAY_BEFORE_POLL_JOB_STATUS,
        } = options;
        setJobOptions(options);
        clearPollJobStatusInterval();
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
        }
        const asyncOperation = async () => {
          try {
            setIsFetching(true);
            const response = await requestDataQuery(request).unwrap();
            jobIdRef.current = response.jobId;
            timeoutIdRef.current = setTimeout(
              startPollJobStatusInterval,
              delayBeforePollJobStatus
            );
            bindChannelRef.current();
            return response;
          } catch (err) {
            handleErrorResponse(err, options);
          }
          return null;
        };

        asyncOperation();

        return new Promise<ResponseType | undefined>((resolve, reject) => {
          promiseRef.current = { resolve, reject };
        }).finally(() => {
          if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
          }
        });
      },
      [requestDataQuery]
    );

  useEffect(() => {
    bindChannelRef.current();
    return () => {
      unbindChannelRef.current();
    };
  }, []);

  const pollJobStatus = useCallback(async () => {
    if (jobIdRef.current) {
      try {
        const fetchedData = await getJobStatus({
          jobId: jobIdRef.current,
        }).unwrap();
        if (
          fetchedData.status === AsyncJobStatus.done ||
          fetchedData.status === AsyncJobStatus.error
        ) {
          fetchData(jobIdRef.current);
          clearPollJobStatusInterval();
        }
      } catch (err) {
        handleErrorResponse(err, jobOptions);
        clearPollJobStatusInterval();
      }
    }
  }, [getJobStatus, retrieveData, jobOptions]);

  const {
    start: startPollJobStatusInterval,
    clear: clearPollJobStatusInterval,
  } = useInterval({
    callback: pollJobStatus,
    interval: JOB_STATUS_POLL_INTERVAL,
    autoStart: false,
  });

  return [
    requestData,
    {
      data:
        jobQueryInfo?.data?.status !== AsyncJobStatus.error
          ? jobQueryInfo?.data?.data
          : null,
      isLoading: jobQueryInfo.isLoading || requestQueryInfo.isLoading,
      isSuccess:
        jobQueryInfo.isSuccess &&
        requestQueryInfo.isSuccess &&
        jobQueryInfo?.data?.status !== AsyncJobStatus.error,
      isError:
        jobQueryInfo.isError ||
        requestQueryInfo.isError ||
        jobQueryInfo?.data?.status === AsyncJobStatus.error,
      isFetching,
      jobMessage,
      error:
        jobQueryInfo.error ||
        requestQueryInfo.error ||
        (jobQueryInfo?.data?.status === AsyncJobStatus.error
          ? jobQueryInfo?.data?.data
          : null),
    },
  ];
};
